import classNames from "classnames";
import { useFormContext } from "react-hook-form";
import { Button, ButtonKind, Heading } from "../../../../../core/ui";
import { CANADA_PROVINCES_AND_TERRITORIES, US_STATES_AND_TERRITORIES } from "@inferno/renderer-shared-core";
import { FORM_ERRORS, SelectInput, SelectOption, SimpleInput } from "../../forms";

interface CoastSignUpBillingInfoProps {
  goForward: () => void;
  onCancel: () => void;
  goBack: () => void;
  stepNumber: number;
  stepCount: number;
  country: string;
  hidden: boolean;
}

export interface CoastSignUpBillingInfoInputs {
  address1: string;
  address2: string;
  firstName: string;
  lastName: string;
  company: string;
  isGift: boolean;
  state: string;
  phone: string;
  city: string;
  zip: string;
}

const getStatesDropdownOptions = (country?: string) =>
  (country === "CA" ? CANADA_PROVINCES_AND_TERRITORIES : US_STATES_AND_TERRITORIES).map(({ label, value }) => (
    <SelectOption label={label} value={value} key={label} />
  ));

export function CoastSignUpBillingInfo({
  goForward,
  onCancel,
  goBack,
  stepNumber,
  stepCount,
  hidden,
  country,
}: CoastSignUpBillingInfoProps) {
  const { register, handleSubmit, errors } = useFormContext<CoastSignUpBillingInfoInputs>();

  return (
    <section className={classNames("component-pick-your-plan", "sign-up-step", { hidden })}>
      <p className="step-indicator">
        Step {stepNumber} of {stepCount}
      </p>

      <Heading level={2} className="section-title">
        Enter Your Billing Info
      </Heading>

      <form onSubmit={handleSubmit(goForward)}>
        <div className="account-form-row">
          <SimpleInput
            placeholder="Enter your first name"
            rules={{ required: FORM_ERRORS.required }}
            error={errors.firstName}
            register={register}
            label="First Name"
            name="firstName"
            type="text"
            className="column-6"
          />

          <SimpleInput
            placeholder="Enter your last name"
            rules={{ required: FORM_ERRORS.required }}
            error={errors.lastName}
            register={register}
            label="Last Name"
            name="lastName"
            type="text"
            className="column-6"
          />
        </div>

        <SimpleInput
          placeholder="Enter your company"
          error={errors.company}
          register={register}
          optional={true}
          label="Company"
          name="company"
          type="text"
        />

        <SimpleInput
          placeholder="Enter phone number"
          optional={true}
          error={errors.phone}
          register={register}
          label="Phone"
          name="phone"
          type="tel"
        />

        <SimpleInput
          placeholder="Address line 1"
          rules={{ required: FORM_ERRORS.required }}
          error={errors.address1}
          register={register}
          label="Billing Address"
          name="address1"
          type="text"
        />

        <SimpleInput
          placeholder="Address line 2"
          error={errors.address2}
          register={register}
          name="address2"
          type="text"
        />

        <div className="account-form-row">
          <SimpleInput
            placeholder="Enter your city"
            rules={{ required: FORM_ERRORS.required }}
            error={errors.city}
            register={register}
            label="City"
            name="city"
            type="text"
            className="column-4"
          />

          <SelectInput
            register={register}
            rules={{ required: FORM_ERRORS.required }}
            error={errors.state}
            placeholder={{ label: "Select...", value: "" }}
            label={country === "CA" ? "Province" : "State"}
            className="column-4"
            name="state"
          >
            {getStatesDropdownOptions(country)}
          </SelectInput>

          <SimpleInput
            placeholder={country === "CA" ? "Postal Code" : "Zip Code"}
            rules={{ required: FORM_ERRORS.required }}
            error={errors.zip}
            register={register}
            type="text"
            label={country === "CA" ? "Postal Code" : "Zip Code"}
            name="zip"
            className="column-4"
            size={10}
          />
        </div>

        <div className="button-prev-next">
          <Button
            disabled={!!Object.values(errors).length}
            kind={ButtonKind.CUSTOMIZED}
            className="button-continue"
            type="submit"
          >
            Continue
          </Button>
          <Button kind={ButtonKind.CUSTOMIZED} className="button-previous" click={goBack}>
            Previous
          </Button>
        </div>
      </form>
      <Button kind={ButtonKind.CUSTOMIZED} className="button-cancel" click={onCancel}>
        Cancel
      </Button>
    </section>
  );
}
